import type {
  Assignment,
  BlockType,
  MaterialItem,
  ParticipantStance,
  StudyBlock
} from "../types/study";

function hashSeed(input: string): number {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createRandom(seed: string): () => number {
  let value = hashSeed(seed);
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffled<T>(items: T[], random: () => number): T[] {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }
  return copy;
}

function pickUniqueFamilies(items: MaterialItem[]): MaterialItem[] {
  const seen = new Set<string>();
  const picked: MaterialItem[] = [];
  for (const item of items) {
    if (seen.has(item.family_id)) {
      continue;
    }
    seen.add(item.family_id);
    picked.push(item);
  }
  return picked;
}

export function createAssignment(
  materials: MaterialItem[],
  stance: ParticipantStance,
  seed: string
): Assignment {
  const random = createRandom(seed);
  const blockTypes: BlockType[] = ["non_meta", "non_meta", "meta", "meta"];
  const keyPool = pickUniqueFamilies(
    shuffled(
      materials.filter((item) => item.item_role === "key" && item.stance_version === stance),
      random
    )
  );
  const fillerPool = shuffled(
    materials.filter((item) => item.item_role === "filler" || item.item_role === "anchor"),
    random
  );

  const blocks: StudyBlock[] = blockTypes.map((blockType, blockIndex) => {
    const keyStart = blockIndex * 6;
    const fillerStart = blockIndex * 3;
    const trials = shuffled(
      [...keyPool.slice(keyStart, keyStart + 6), ...fillerPool.slice(fillerStart, fillerStart + 3)],
      random
    );

    return {
      blockIndex,
      blockType,
      trials
    };
  });

  return {
    seed,
    blocks
  };
}
